/**
 * Demo tag catalog. Six purpose-built tags that map cleanly onto the
 * 30 demo scenarios so the presentation page's group-by-tag layout
 * (legacy parity) renders meaningfully out of the box.
 *
 * Each tag carries a numeric id (matching live `scenario_tag.id`),
 * a `title`, a hex `color` for the chip backgrounds, and an `icon`
 * slug (currently informational — the SPA doesn't render tag icons
 * yet, but the engine catalogues them for future use).
 *
 * Mapping from scenario → tag(s) lives in
 * `assignTagsToScenario(fusionId)` below, called by the scenarios
 * fixture builder.
 */

type Row = Record<string, unknown>;

interface DemoTag {
  id: number;
  title: string;
  color: string;
  icon: string;
}

export const DEMO_TAGS_CATALOG: ReadonlyArray<DemoTag> = [
  // Colors + icons map to the legacy catalogue
  // (`wf-audit/src/slices/presentation/constants/tagConstants.ts` +
  // `/img/tags/Icon_N.svg`). Picking distinct glyphs so each demo
  // group is visually distinguishable in the scenario tag selector
  // + presentation page tag headers.
  { id: 901, title: 'Operations', color: '#007CEE', icon: 'Icon_5.svg' },
  { id: 902, title: 'Customer Success', color: '#008F60', icon: 'Icon_12.svg' },
  { id: 903, title: 'Finance', color: '#E0B22E', icon: 'Icon_28.svg' },
  { id: 904, title: 'AI Automation', color: '#9C5BEF', icon: 'Icon_44.svg' },
  { id: 905, title: 'Maintenance', color: '#464646', icon: 'Icon_19.svg' },
  { id: 906, title: 'People & HR', color: '#EC4081', icon: 'Icon_36.svg' },
];

const TAG_BY_TITLE: Map<string, DemoTag> = new Map(
  DEMO_TAGS_CATALOG.map((t) => [t.title.toLowerCase(), t]),
);

/**
 * Looser shape returned from `useTags()` — the SPA's hook unwraps
 * `{tags: [...]}` so the demo fixture surfaces the same flat array.
 */
export const DEMO_TAGS: ReadonlyArray<Row> = DEMO_TAGS_CATALOG.map((t) => ({
  id: t.id,
  title: t.title,
  description: null,
  color: t.color,
  icon: t.icon,
  scenarioFusionIds: null, // engine surfaces a CSV string here in production
}));

/**
 * Per-fusion-id tag assignment. Keyed by `fusionId` (the same value
 * `DEMO_SCENARIOS[*].fusionId` carries). Multiple tags per scenario
 * are intentional — the presentation page's group-by-tag duplicates
 * such cards across each tag section, which gives the demo a
 * realistic "this scenario serves Operations AND Finance" feel.
 *
 * Five scenarios are deliberately left untagged — they fall into the
 * `Not tagged` group on the presentation list, giving the
 * grouping UX something to demo on both sides.
 */
const ASSIGNMENTS: Record<string, ReadonlyArray<string>> = {
  '101001': ['Operations'],
  '101002': ['Operations'],
  '101003': ['Operations'],
  '101004': ['Customer Success', 'AI Automation'],
  '101005': ['Operations'],
  '101006': ['Operations'],
  // 101007 + 101008 inactive — untagged (legacy parity: KPIs + tags
  // typically only attach to active scenarios).
  '101009': ['Operations'],
  '101010': ['Operations'],
  '101011': ['Customer Success'],
  '101012': ['Customer Success'],
  '101013': ['People & HR'],
  '101014': ['Finance'],
  '101015': ['Customer Success'],
  '101016': ['AI Automation'],
  '101017': ['AI Automation', 'Operations'],
  '101018': ['AI Automation'],
  '101019': ['AI Automation'],
  '101020': ['Operations'],
  '101021': ['Maintenance'],
  '101022': ['Maintenance'],
  '101023': ['Customer Success'],
  '101024': ['Customer Success'],
  '101025': ['Finance'],
  // 101026 inactive — untagged
  '101027': ['People & HR'],
  '101028': ['Finance'],
  '101029': ['Operations'],
  // 101030 inactive — untagged
};

/**
 * Returns the tag objects attached to a demo scenario in the shape
 * the engine's `/v1/scenarios/{id}` detail response carries:
 * `{id, title, color, icon}`. Used by the scenarios fixture
 * builder's `tags:` field. Empty array for unmapped scenarios.
 */
export function tagsForScenario(fusionId: string): ReadonlyArray<Row> {
  const titles = ASSIGNMENTS[fusionId];
  if (!titles) return [];
  return titles.flatMap((title) => {
    const tag = TAG_BY_TITLE.get(title.toLowerCase());
    if (!tag) return [];
    return [{ id: tag.id, title: tag.title, color: tag.color, icon: tag.icon }];
  });
}
